import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Menu, MenuItem } from "@repo/components/src/ui/menu";
import { AlertCircleIcon, ChevronDown, LucideIcon } from "lucide-react-native";
import React, { ComponentProps, useState } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { Box } from "../../ui/box";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { Heading } from "../../ui/heading";
import { Text } from "../../ui/text";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

type Props<TFieldValues extends FieldValues> =
  FormInputSelectProps<TFieldValues> & {
    headingProps?: ComponentProps<typeof Heading>;
    menuProps?: Omit<ComponentProps<typeof Menu>, "trigger">;
    buttonTriggerProps?: Omit<
      ComponentProps<typeof Button>,
      "children" | "onPress"
    >;
    buttonTextTriggerProps?: Omit<
      ComponentProps<typeof ButtonText>,
      "children"
    >;
    buttonTriggerIcon?: LucideIcon;
    labelTextProps?: ComponentProps<typeof FormControlLabelText>;
    useCompleteButtonWidth?: boolean;
  };

export default function FormInputMenu<TFieldValues extends FieldValues>(
  props: Props<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    placeholder,
    headingProps,
    menuProps,
    buttonTriggerProps,
    buttonTriggerIcon = ChevronDown,
    buttonTextTriggerProps,
    labelTextProps,
    useCompleteButtonWidth,
  } = props;
  const [layoutWidth, setLayoutWidth] = useState<number>(1);
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled}
          className={className}
          size={size}
        >
          {label && (
            <FormControlLabel>
              <FormControlLabelText
                size={labelSize || size}
                {...labelTextProps}
              >
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <Box ref={focusScrollRef(ref)}>
            <Menu
              placement="bottom"
              offset={5}
              closeOnSelect={true}
              className={"rounded-lg w-full"}
              {...menuProps}
              style={[
                {
                  width: useCompleteButtonWidth ? layoutWidth : undefined,
                },
                menuProps?.style,
              ]}
              trigger={({ ...triggerProps }) => {
                const title = options.find(
                  (option) => option.value === value,
                )?.label;
                return (
                  <Button
                    {...triggerProps}
                    variant="outline"
                    rounded="2xl"
                    action="muted"
                    size={size}
                    className={label ? "" : ""}
                    onLayout={(event) => {
                      setLayoutWidth(event.nativeEvent.layout.width);
                    }}
                    {...buttonTriggerProps}
                  >
                    <ButtonText
                      className={`flex-1 shrink ${!title ? "text-typography-500" : ""} font-normal`}
                      {...buttonTextTriggerProps}
                    >
                      {title || placeholder}
                    </ButtonText>
                    <ButtonIcon as={buttonTriggerIcon} />
                  </Button>
                );
              }}
            >
              {options.map((content) => {
                return (
                  <React.Fragment key={content.value}>
                    <MenuItem
                      className="rounded-lg"
                      onPress={() => onChange(content.value)}
                      key={`${content.value}-item`}
                    >
                      <Box>
                        <Heading size="sm" bold {...headingProps}>
                          {content.label}
                        </Heading>
                        <Text size="sm" className="mt-1">
                          {content.description}
                        </Text>
                      </Box>
                    </MenuItem>
                  </React.Fragment>
                );
              })}
            </Menu>
          </Box>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
